import { useState, useEffect, useCallback } from 'react';
import constituencyService from '../services/constituencyService';

// Custom hook for constituency data management
export const useConstituencyData = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [constituencies, setConstituencies] = useState([]);
  const [selectedConstituency, setSelectedConstituency] = useState(null);
  const [currentData, setCurrentData] = useState(null);

  // Load constituency list
  const loadConstituencies = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await constituencyService.getConstituencyList();
      setConstituencies(data);
      
      // Set default constituency
      if (data.length > 0 && !selectedConstituency) {
        const defaultConstituency = data.find(c => 
          c.fullName.toLowerCase().includes('patna') || 
          c.fullName.toLowerCase().includes('delhi')
        ) || data[0];
        setSelectedConstituency(defaultConstituency.code);
      }
    } catch (err) {
      setError('Failed to load constituency data');
      console.error('Error loading constituencies:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedConstituency]);

  // Load specific constituency data
  const loadConstituencyData = useCallback(async (constituencyCode) => {
    if (!constituencyCode) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const data = await constituencyService.getConstituencyData(constituencyCode);
      setCurrentData(data);
    } catch (err) {
      setError(`Failed to load data for constituency: ${constituencyCode}`);
      console.error('Error loading constituency data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Change selected constituency
  const changeConstituency = useCallback(async (constituencyCode) => {
    setSelectedConstituency(constituencyCode);
    await loadConstituencyData(constituencyCode);
  }, [loadConstituencyData]);

  // Search constituencies
  const searchConstituencies = useCallback(async (query) => {
    try {
      return await constituencyService.searchConstituencies(query);
    } catch (err) {
      console.error('Error searching constituencies:', err);
      return [];
    }
  }, []);

  // Get constituencies by state
  const getConstituenciesByState = useCallback(async (state) => {
    try {
      return await constituencyService.getConstituenciesByState(state);
    } catch (err) {
      console.error('Error getting constituencies by state:', err);
      return [];
    }
  }, []);

  // Load initial data
  useEffect(() => {
    loadConstituencies();
  }, [loadConstituencies]);

  // Load constituency data when selection changes
  useEffect(() => {
    if (selectedConstituency) {
      loadConstituencyData(selectedConstituency);
    }
  }, [selectedConstituency, loadConstituencyData]);

  return {
    loading,
    error,
    constituencies,
    selectedConstituency,
    currentData,
    changeConstituency,
    searchConstituencies,
    getConstituenciesByState,
    reload: loadConstituencies
  };
};

// Hook for summary statistics
export const useSummaryStats = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadStats = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const summaryStats = await constituencyService.getSummaryStats();
      setStats(summaryStats);
    } catch (err) {
      setError('Failed to load summary statistics');
      console.error('Error loading summary stats:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  return { stats, loading, error, reload: loadStats };
};

// Hook for constituency search and filtering
export const useConstituencySearch = () => {
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const search = useCallback(async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    setLoading(true);
    setSearchQuery(query);
    
    try {
      const results = await constituencyService.searchConstituencies(query);
      setSearchResults(results);
    } catch (err) {
      console.error('Search error:', err);
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const clearSearch = useCallback(() => {
    setSearchResults([]);
    setSearchQuery('');
  }, []);

  return {
    searchResults,
    loading,
    searchQuery,
    search,
    clearSearch
  };
};
